/**
 * A published Design Builder dashboard, as `/dvm-published/:slug` shows it.
 *
 * Runs on the same pieces as the builder's Preview, so a published design
 * behaves exactly like it did there:
 * - the `layout_json` is parsed back into a design document held by its own
 *   `DesignDocumentStore` (one per view, never the builder's);
 * - `DesignRuntimeService` drives filters, cross-filter and input recompute;
 * - a hidden `DesignBuildHostComponent` is the recompute applier — the family
 *   steps that turn a compute result into tile data, as on the canvas.
 *
 * It fetches no builder config: the published widgets carry their own
 * definitions, so the built-in layout plus the page's compute URL is enough.
 */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';

import type { BuilderLayoutConfig } from '../../shared/self-service-config.types';
import { DVM_LAYOUT_BUILDER_DEFAULT_LAYOUT } from '../../dvm-layout-builder/dvm-builder-config.utils';
import { DesignBuildHostComponent } from '../data/design-build-host.component';
import { DesignRuntimeService, type DashboardNavigationFilter } from '../data/design-runtime.service';
import { DesignDataService } from '../services/design-data.service';
import { DesignDocumentStore } from '../state/design-document.store';
import { parseDesignDocument } from '../utils/design-json.utils';
import { pageScreensOf, publishedLayoutAsDocumentJson } from '../utils/design-publish.utils';
import { DesignScreenPopupComponent } from './design-screen-popup.component';
import { DesignScreenPreviewComponent } from './design-screen-preview.component';

@Component({
  selector: 'app-design-published-view',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, DesignScreenPreviewComponent, DesignScreenPopupComponent, DesignBuildHostComponent],
  providers: [DesignDocumentStore, DesignDataService, DesignRuntimeService],
  templateUrl: './design-published-view.component.html',
  styleUrl: './design-published-view.component.scss',
})
export class DesignPublishedViewComponent {
  /** The `layout_json` of the published row. */
  readonly layoutJson = input.required<Record<string, unknown>>();
  /** The published view's compute endpoint (dvm-published-view.json). */
  readonly computeApi = input('/components/compute');
  readonly navigationFilter = input<DashboardNavigationFilter | null>(null);
  readonly navigationFilterCleared = output<void>();

  private readonly doc = inject(DesignDocumentStore);
  private readonly runtime = inject(DesignRuntimeService);

  readonly error = signal('');
  /** Popup screens are opened by their buttons, not shown as tabs. */
  // Layers left outside every frame ("Page (loose items)") are scraps on the
  // artboard, not a screen — published, they would add a tab bar. They only
  // show when the page has no frame at all.
  readonly screens = computed(() => {
    const screens = pageScreensOf(this.doc.document());
    const framed = screens.filter((screen) => !screen.id.endsWith('_root'));
    return framed.length ? framed : screens;
  });
  private readonly activeScreenId = signal('');
  readonly activeScreen = computed(() => {
    const screens = this.screens();
    return screens.find((screen) => screen.id === this.activeScreenId()) ?? screens[0] ?? null;
  });

  constructor() {
    effect(() => {
      if (this.runtime.navigationReset()) untracked(() => this.navigationFilterCleared.emit());
    });
    const dataService = inject(DesignDataService);
    effect(() => {
      const layout = publishedLayoutConfig(this.computeApi());
      untracked(() => {
        dataService.setLayout(layout);
        this.runtime.setLayout(layout);
      });
    });

    effect(() => {
      const layoutJson = this.layoutJson();
      const navigationFilter = this.navigationFilter();
      untracked(() => {
        this.load(layoutJson);
        this.runtime.setNavigationFilter(navigationFilter);
      });
    });

    // A Navigate → Screen button.
    effect(() => {
      const request = this.runtime.screenRequest();
      if (request && this.screens().some((screen) => screen.id === request.screenId)) {
        untracked(() => this.activeScreenId.set(request.screenId));
      }
    });
  }

  selectScreen(id: string): void {
    this.activeScreenId.set(id);
  }

  /** Clear every filter / selection, then re-fetch the unfiltered values. */
  reset(): void {
    this.runtime.refreshDashboard(true);
  }

  private load(layoutJson: Record<string, unknown>): void {
    const { document, error } = parseDesignDocument(JSON.stringify(publishedLayoutAsDocumentJson(layoutJson)));
    if (!document) {
      this.error.set(error ?? 'This dashboard could not be read.');
      return;
    }
    this.error.set('');
    this.doc.resetDocument(document);
    this.activeScreenId.set('');
  }
}

/** The built-in Layout Builder layout, pointed at the published view's compute API. */
function publishedLayoutConfig(computeApi: string): BuilderLayoutConfig {
  return {
    ...DVM_LAYOUT_BUILDER_DEFAULT_LAYOUT,
    apiEndpoints: { ...DVM_LAYOUT_BUILDER_DEFAULT_LAYOUT.apiEndpoints, generateConfigApi: computeApi },
  } as BuilderLayoutConfig;
}
